import React, { useState, useEffect, Component, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { toast } from '../../components/Toast/toast';
import { Button } from '../../components/Buttons';
import { Select, Table, Input, Collapse } from 'antd';
// import { OogwaiLogo3 } from '../../components/Icons/iconImages';
import { TableData } from '../../components/Table';
import { DynamicTable } from '../../components/Table/NestedTable';
import CustomChart from '../../components/ChartComponents/BarChart';
import {
  CustomBarChart,
  CustomMultiBarChart,
  CustomMultiBarChartWithLine,
  CustomBarChartWithLine,
  CustomMultiLineChartWithSecondaryAxis,
  CustomAreaChart,
  CustomPieChart,
  CustomHorizontalBarChart,
  CustomMultiHorizontalBarChart,
  CustomDonutChart,
  CustomScatterPlot,
  CustomGeoMap
} from '../../components/ui/custom-chart';

const { TextArea } = Input;

// Error Boundary component for catching render errors
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

const ComponentTestPlayground = () => {
  const navigate = useNavigate();
  const userData = useSelector((state: RootState) => state.userData.user as { name?: string; email?: string; uid?: string } | null);
  const [jsonInput, setJsonInput] = useState('');
  const [parsedData, setParsedData] = useState<any>(null);
  const [selectedComponent, setSelectedComponent] = useState('table');
  const [textAreaValue, setTextAreaValue] = useState('');
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  // Check if user has @oogw.ai email
  const userEmail = localStorage.getItem("oogw.ai_user_email") || userData?.email;
  
  useEffect(() => {
    if (!userEmail || !userEmail.endsWith('@oogw.ai')) {
      toast.error('This page is only accessible to @oogw.ai users');
      navigate('/dashboard');
    }
  }, [userEmail, navigate]);

  // Sample data for different components
  const sampleData = {
    table: [
      { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'Active' },
      { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'User', status: 'Active' },
      { id: 3, name: 'Bob Wilson', email: 'bob@example.com', role: 'Editor', status: 'Inactive' },
    ],
    tableData: [
      { id: 1, product: 'Laptop', price: 999.99, quantity: 5, category: 'Electronics' },
      { id: 2, product: 'Mouse', price: 29.99, quantity: 50, category: 'Accessories' },
      { id: 3, product: 'Keyboard', price: 79.99, quantity: 15, category: 'Accessories' },
      { id: 4, product: 'Monitor', price: 299.99, quantity: 8, category: 'Electronics' },
      { id: 5, product: 'Headphones', price: 149.99, quantity: 20, category: 'Audio' },
    ],
    visualTag: {
      timestamp: new Date().toISOString(),
      chat: [
        {
          visual_tag: 'text',
          data: [],
          text_response: '**Sample Response:** This is a test message with **bold text** and regular text.',
          agent: 'oogwai'
        },
        {
          visual_tag: 'table',
          data: [
            { metric: 'Users', value: '1,234', change: '+12%' },
            { metric: 'Sessions', value: '5,678', change: '-3%' },
            { metric: 'Bounce Rate', value: '45.6%', change: '+2.1%' }
          ],
          text_response: 'Here are your analytics metrics:',
          agent: 'googleanalytics'
        },
        {
          visual_tag: 'table',
          data: [
            { item: 'First item in the list' },
            { item: 'Second item with more content' },
            { item: 'Third item' }
          ],
          text_response: 'Key points to consider:',
          agent: 'meta'
        }
      ]
    },
    visualTagText: {
      timestamp: new Date().toISOString(),
      chat: [
        {
          visual_tag: 'text',
          data: [],
          text_response: '**Welcome!** Here is a simple text response with **bold formatting** and regular text. You can also have *italic text* and combine ***bold italic*** text.',
          agent: 'oogwai'
        }
      ]
    },
    visualTagTable: {
      timestamp: new Date().toISOString(),
      chat: [
        {
          visual_tag: 'table',
          data: [
            { date: '2024-01-01', revenue: '$12,345', orders: 234, conversion: '3.4%' },
            { date: '2024-01-02', revenue: '$15,678', orders: 312, conversion: '4.1%' },
            { date: '2024-01-03', revenue: '$9,012', orders: 156, conversion: '2.8%' },
            { date: '2024-01-04', revenue: '$18,901', orders: 423, conversion: '5.2%' }
          ],
          text_response: '**Sales Performance:** Last 4 days summary',
          agent: 'shopify'
        }
      ]
    },
    visualTagLineGraph: {
      timestamp: new Date().toISOString(),
      chat: [
        {
          visual_tag: 'line-graph',
          data: [
            { date: '2024-01-01', sessions: 1234 },
            { date: '2024-01-02', sessions: 1456 },
            { date: '2024-01-03', sessions: 1678 },
            { date: '2024-01-04', sessions: 1890 },
            { date: '2024-01-05', sessions: 2012 }
          ],
          text_response: '**Traffic Trend:** Last 5 days showing upward trend in sessions',
          agent: 'googleanalytics'
        }
      ]
    },
    visualTagBarChart: {
      timestamp: new Date().toISOString(),
      chat: [
        {
          visual_tag: 'bar-chart',
          data: [
            { month: 'Jan', revenue: 4000 },
            { month: 'Feb', revenue: 3000 },
            { month: 'Mar', revenue: 5000 },
            { month: 'Apr', revenue: 4500 },
            { month: 'May', revenue: 6000 },
            { month: 'Jun', revenue: 5500 }
          ],
          text_response: '**Revenue Trend:** First half of 2024',
          agent: 'meta'
        }
      ]
    },
    visualTagMixed: {
      timestamp: new Date().toISOString(),
      chat: [
        {
          visual_tag: 'text',
          data: [],
          text_response: '**Analysis Complete!** Here are your results:',
          agent: 'oogwai'
        },
        {
          visual_tag: 'table',
          data: [
            { metric: 'Total Users', current: '5,234', previous: '4,123', change: '+27%' },
            { metric: 'Page Views', current: '45,678', previous: '38,901', change: '+17%' },
            { metric: 'Avg Duration', current: '3:45', previous: '3:12', change: '+17%' }
          ],
          text_response: 'Key metrics comparison:',
          agent: 'googleanalytics'
        },
        {
          visual_tag: 'bar-chart',
          data: [
            { channel: 'Direct', sessions: 2345 },
            { channel: 'Organic', sessions: 3456 },
            { channel: 'Social', sessions: 1234 },
            { channel: 'Paid', sessions: 890 }
          ],
          text_response: '**Traffic Sources Performance:**',
          agent: 'googleanalytics'
        },
        {
          visual_tag: 'text',
          data: [],
          text_response: '💡 **Recommendation:** Focus on mobile optimization to capitalize on the growing mobile traffic.',
          agent: 'oogwai'
        }
      ]
    },
    rawData: {
      googleanalytics: [
        { date: '2024-01-01', users: 100, sessions: 150, bounceRate: 45.2 },
        { date: '2024-01-02', users: 120, sessions: 180, bounceRate: 42.1 },
        { date: '2024-01-03', users: 95, sessions: 140, bounceRate: 48.3 }
      ],
      shopify: [
        { orderId: 'ORD-001', customer: 'Alice Brown', total: 299.99, status: 'Shipped' },
        { orderId: 'ORD-002', customer: 'Charlie Davis', total: 149.50, status: 'Processing' },
        { orderId: 'ORD-003', customer: 'Eve Johnson', total: 599.00, status: 'Delivered' }
      ]
    },
    // teamMembers: [
    //   { id: '1', name: 'Alice Admin', email: 'alice@oogw.ai', profile_picture: '', admin: true },
    //   { id: '2', name: 'Bob User', email: 'bob@oogw.ai', profile_picture: '', admin: false },
    //   { id: '3', name: 'Charlie Editor', email: 'charlie@oogw.ai', profile_picture: '', admin: false }
    // ],
    dynamicTable: [
      { id: 1, product: 'Laptop', price: 999.99, quantity: 5, category: 'Electronics', inStock: true },
      { id: 2, product: 'Mouse', price: 29.99, quantity: 50, category: 'Accessories', inStock: true },
      { id: 3, product: 'Keyboard', price: 79.99, quantity: 0, category: 'Accessories', inStock: false },
      { id: 4, product: 'Monitor', price: 299.99, quantity: 12, category: 'Electronics', inStock: true },
      { id: 5, product: 'Headphones', price: 149.99, quantity: 8, category: 'Audio', inStock: true }
    ],
    textAreaSample: "This is a sample text for the TextArea component.\n\nIt supports multiple lines and can be used for longer text input.\n\nYou can customize its properties like rows, maxLength, showCount, etc.",
    pieChartData: [
      { category: 'Desktop', value: 45234 },
      { category: 'Mobile', value: 32156 },
      { category: 'Tablet', value: 12567 },
      { category: 'Smart TV', value: 5432 },
      { category: 'Other', value: 2109 }
    ],
    horizontalBarData: [
      { browser: 'Chrome', users: 68234 },
      { browser: 'Safari', users: 45123 },
      { browser: 'Firefox', users: 23456 },
      { browser: 'Edge', users: 15678 },
      { browser: 'Opera', users: 8901 }
    ],
    verticalBarData: [
      { category: 'Q1', value: 12500 },
      { category: 'Q2', value: 18700 },
      { category: 'Q3', value: 15200 },
      { category: 'Q4', value: 22100 },
    ],
    multiBarData: [
      { month: 'Jan', revenue: 4500, profit: 2100, expenses: 2400 },
      { month: 'Feb', revenue: 5200, profit: 2800, expenses: 2400 },
      { month: 'Mar', revenue: 4800, profit: 2300, expenses: 2500 },
      { month: 'Apr', revenue: 6100, profit: 3200, expenses: 2900 },
      { month: 'May', revenue: 7200, profit: 4100, expenses: 3100 },
      { month: 'Jun', revenue: 6800, profit: 3700, expenses: 3100 }
    ],
    areaChartData: [
      { date: '2024-01-01', visitors: 3456 },
      { date: '2024-01-02', visitors: 4123 },
      { date: '2024-01-03', visitors: 3890 },
      { date: '2024-01-04', visitors: 4567 },
      { date: '2024-01-05', visitors: 5234 },
      { date: '2024-01-06', visitors: 4890 },
      { date: '2024-01-07', visitors: 5678 }
    ],
    donutChartData: [
      { category: 'Direct', value: 45234 },
      { category: 'Organic Search', value: 32156 },
      { category: 'Social Media', value: 22567 },
      { category: 'Email', value: 15432 },
      { category: 'Referral', value: 12109 },
      { category: 'Paid Ads', value: 8901 }
    ],
    scatterPlotData: [
      { price: 10, sales: 20, product: 'Product A' },
      { price: 15, sales: 35, product: 'Product A' },
      { price: 20, sales: 40, product: 'Product A' },
      { price: 25, sales: 30, product: 'Product A' },
      { price: 30, sales: 25, product: 'Product A' },
      { price: 12, sales: 28, product: 'Product B' },
      { price: 18, sales: 42, product: 'Product B' },
      { price: 22, sales: 38, product: 'Product B' },
      { price: 28, sales: 32, product: 'Product B' },
      { price: 35, sales: 20, product: 'Product B' },
      { price: 8, sales: 15, product: 'Product C' },
      { price: 14, sales: 25, product: 'Product C' },
      { price: 19, sales: 30, product: 'Product C' },
      { price: 24, sales: 28, product: 'Product C' },
      { price: 32, sales: 18, product: 'Product C' }
    ],
    geoMapData: [
      { country: 'United States', value: 125432 },
      { country: 'China', value: 98765 },
      { country: 'India', value: 76543 },
      { country: 'United Kingdom', value: 65234 },
      { country: 'Germany', value: 54321 },
      { country: 'France', value: 45678 },
      { country: 'Japan', value: 43210 },
      { country: 'Canada', value: 34567 },
      { country: 'Russia', value: 32109 },
      { country: 'Brazil', value: 29876 },
      { country: 'Australia', value: 28901 },
      { country: 'Italy', value: 23456 },
      { country: 'Spain', value: 21098 },
      { country: 'Mexico', value: 19321 },
      { country: 'South Korea', value: 18765 },
      { country: 'Turkey', value: 16543 },
      { country: 'Argentina', value: 14321 },
      { country: 'South Africa', value: 12345 },
      { country: 'Saudi Arabia', value: 11234 },
      { country: 'Egypt', value: 9876 }
    ],
    dualAxisData: [
      { month: 'Jan', revenue: 125000, orders: 450, conversionRate: 3.2 },
      { month: 'Feb', revenue: 148000, orders: 520, conversionRate: 3.5 },
      { month: 'Mar', revenue: 172000, orders: 680, conversionRate: 4.1 },
      { month: 'Apr', revenue: 165000, orders: 590, conversionRate: 3.8 },
      { month: 'May', revenue: 198000, orders: 740, conversionRate: 4.5 },
      { month: 'Jun', revenue: 215000, orders: 820, conversionRate: 4.8 }
    ],
    multiBarSecondaryYAxis: [
      { month: 'Jan', revenue: 125000, expenses: 95000, profit: 30000, growthRate: 5.2 },
      { month: 'Feb', revenue: 148000, expenses: 108000, profit: 40000, growthRate: 8.5 },
      { month: 'Mar', revenue: 172000, expenses: 121000, profit: 51000, growthRate: 12.1 },
      { month: 'Apr', revenue: 165000, expenses: 115000, profit: 50000, growthRate: 9.8 },
      { month: 'May', revenue: 198000, expenses: 135000, profit: 63000, growthRate: 15.2 },
      { month: 'Jun', revenue: 215000, expenses: 142000, profit: 73000, growthRate: 18.5 }
    ],
    lineGraphSecondaryYAxis: [
      { month: 'Jan', temperature: 15, humidity: 65, pressure: 1013, windSpeed: 12 },
      { month: 'Feb', temperature: 18, humidity: 62, pressure: 1015, windSpeed: 14 },
      { month: 'Mar', temperature: 22, humidity: 58, pressure: 1012, windSpeed: 16 },
      { month: 'Apr', temperature: 26, humidity: 55, pressure: 1010, windSpeed: 18 },
      { month: 'May', temperature: 30, humidity: 60, pressure: 1008, windSpeed: 20 },
      { month: 'Jun', temperature: 33, humidity: 68, pressure: 1006, windSpeed: 22 }
    ],
    barGraphSecondaryYAxis: [
      { month: 'Jan', sales: 85000, conversionRate: 3.2 },
      { month: 'Feb', sales: 92000, conversionRate: 3.8 },
      { month: 'Mar', sales: 105000, conversionRate: 4.1 },
      { month: 'Apr', sales: 98000, conversionRate: 3.5 },
      { month: 'May', sales: 118000, conversionRate: 4.6 },
      { month: 'Jun', sales: 132000, conversionRate: 5.2 }
    ]
  };

  const handleJsonParse = () => {
    try {
      // First attempt to parse as regular JSON
      let dataToProcess = jsonInput;

      // Check if the input contains single quotes and looks like Python dict format
      if (jsonInput.includes("'") && jsonInput.includes("{'")) {
        // Replace single quotes with double quotes for JSON parsing
        // But be careful not to replace apostrophes in actual text
        dataToProcess = jsonInput
          .replace(/'/g, '"')  // Replace all single quotes with double quotes
          .replace(/False/g, 'false')  // Python False to JSON false
          .replace(/True/g, 'true')    // Python True to JSON true
          .replace(/None/g, 'null');   // Python None to JSON null
      }

      const parsed = JSON.parse(dataToProcess);
      setParsedData(parsed);
      toast.success('JSON parsed successfully!');
    } catch (error) {
      // If JSON parsing fails, try to evaluate as JavaScript object literal
      try {
        // This is less safe but handles edge cases
        const evaluated = eval(`(${jsonInput})`);
        setParsedData(evaluated);
        toast.success('Data parsed successfully!');
      } catch (evalError) {
        toast.error('Invalid data format. Please check your input.');
        console.error('Parse error:', error, evalError);
      }
    }
  };

  const loadSampleData = (type: string) => {
    const data = sampleData[type as keyof typeof sampleData];
    setJsonInput(JSON.stringify(data, null, 2));
    setParsedData(data);
  };

  // handleTicker function from Conversation screen
  const handleTicker = (data: any) => {
    if (!data || !data.length) return { xLabel: '', yLabel: '', xAxis: '', yAxis: '' };
    const lastItem = data[data.length - 1];
    if (!lastItem) return { xLabel: '', yLabel: '', xAxis: '', yAxis: '' };
    const keys = Object.keys(lastItem);
    const xLabel = keys.length > 0 ? keys[0] : '';
    const yLabelArray = keys.length > 2
      ? keys.filter((item, index) => index > 0 && keys.length > 2 && item !== 'date')
      : [keys[1]];
    const yLabel = Array.isArray(yLabelArray) ? yLabelArray[0] || '' : yLabelArray || '';
    const xAxis = keys.length > 0 ? (keys.find(key => key === 'date') ? 'date' : keys[0]) : '';
    const yAxisArray = keys.length > 2
      ? keys.filter((item, index) => index > 0 && keys.length > 2 && item !== 'date')
      : [keys[1]];
    const yAxis = Array.isArray(yAxisArray) ? yAxisArray[0] || '' : yAxisArray || '';
    return {
      xLabel,
      yLabel,
      xAxis,
      yAxis
    };
  };

  // Exact handleVisualTag function from Conversation screen
  const handleVisualTag = (response: any) => {
    const dataTimestamp = response?.timestamp;

    return response?.chat?.map((data: any) => {
      const hasMoreKeys = data?.data?.length > 0 ? data?.data?.some((item: any) => Object.keys(item)?.length > 2) : false;
      const TextResponse = ({ className, source }: any) => (
        <div className={`flex flex-row ${className || ''}`}>
          <p className='font-normal text-sm text-text-600 whitespace-pre-wrap break-words'>
            {source?.text_response?.split(/(\*\*[\s\S]+?\*\*)/).map((text: string, index: number) => {
              if (text.startsWith('**') && text.endsWith('**')) {
                return <span key={index} className="font-medium">{text.slice(2, -2)}</span>
              }
              return <span key={index}>{text}</span>
            })}
          </p>
          <div className='pt-1'>
          </div>
        </div>
      );

      switch (data?.visual_tag) {
        case 'text':
          return (
            <div className='pt-3 pb-2' key={dataTimestamp}>
              <TextResponse className={''} source={data} />
            </div>
          );
        case 'line-graph':
          return (
            <div className='py-3' key={dataTimestamp}>
              {data.data && data.data.length && (
                <div className='relative flex flex-row gap-2'>
                  <div className={`relative ${hasMoreKeys ? 'max-w-[70%]' : 'max-w-[35%]'}`}>
                    <TableData pageSizeLimit={6} data={data?.data} isFetching={false} isError={false} />
                  </div>
                  {!hasMoreKeys && (
                    <CustomChart
                      className='shadow-sm rounded-2xl relative border border-solid border-[#e9e9eb] p-3'
                      data={data?.data}
                      type={data?.visual_tag}
                      size='small'
                      dataXkey={handleTicker(data?.data).xAxis}
                      dataYKey={handleTicker(data?.data).yAxis}
                      xAxisLabel={handleTicker(data?.data).xLabel}
                      yAxisLabel={handleTicker(data?.data).yLabel}
                    />
                  )}
                </div>)}
              <TextResponse className='pt-4' source={data} />
            </div>
          );
        case 'bar-chart':
          return (
            <div className='py-3' key={dataTimestamp}>
              {data?.data && data?.data.length && <div className='relative flex flex-row gap-2'>
                <div className={`relative ${hasMoreKeys ? 'max-w-[70%]' : 'max-w-[35%]'}`}>
                  <TableData pageSizeLimit={6} className={'mt-0'} data={data?.data} isFetching={false} isError={false} />
                </div>
                {!hasMoreKeys && (
                  <CustomChart
                    className='shadow-sm rounded-2xl relative border border-solid border-[#e9e9eb] p-3'
                    data={data?.data}
                    type={data?.visual_tag}
                    size='small'
                    dataXkey={handleTicker(data?.data).xAxis}
                    dataYKey={handleTicker(data?.data).yAxis}
                    xAxisLabel={handleTicker(data?.data).xLabel}
                    yAxisLabel={handleTicker(data?.data).yAxis}
                  />
                )}
              </div>}
              <TextResponse className='pt-4' source={data} />
            </div>
          );
        case 'table':
          return <div className='py-3' key={dataTimestamp}>
            <TextResponse source={data} />
            {data?.data?.length > 0 &&
              <div className='relative flex flex-row gap-2'>
                <TableData data={data?.data} isFetching={false} isError={false} pageSizeLimit={5} />
              </div>
            }
          </div>

        default:
          return null;
      }
    })
  };

  const renderTableComponent = () => {
    if (!parsedData || !Array.isArray(parsedData)) {
      return <p className='text-gray-500'>Please load table data first</p>;
    }

    const columns = parsedData.length > 0 ? Object.keys(parsedData[0]).map(key => ({
      title: key,
      dataIndex: key,
      key: key,
    })) : [];

    return (
      <Table
        dataSource={parsedData}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    );
  };

  const renderVisualTag = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load visual tag data first</p>;
    }

    return (
      <div className='bg-white p-4 rounded-lg border'>
        {handleVisualTag(parsedData)}
      </div>
    );
  };

  const renderTeamTable = () => {
    if (!parsedData || !Array.isArray(parsedData)) {
      return <p className='text-gray-500'>Please load team member data first</p>;
    }

    return null

    // return (
    //   <TeamTable
    //     data={parsedData}
    //     onEdit={(id) => toast.info(`Edit clicked for ID: ${id}`)}
    //     onDelete={(id) => toast.info(`Delete clicked for ID: ${id}`)}
    //     updateToggle={(checked) => toast.info(`Toggle changed: ${checked}`)}
    //   />
    // );
  };

  const renderRawDataTable = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load raw data first</p>;
    }

    // const integrationId = parsedData.googleanalytics ? 'googleanalytics' : 
    //                       parsedData.shopify ? 'shopify' : 
    //                       parsedData.meta ? 'meta' : 'none';

    return null;
  };

  const renderTableData = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load table data first</p>;
    }

    try {
      let dataToRender;
      
      if (Array.isArray(parsedData)) {
        dataToRender = parsedData;
      } else if (parsedData.googleanalytics) {
        dataToRender = parsedData.googleanalytics;
      } else if (parsedData.shopify) {
        dataToRender = parsedData.shopify;
      } else if (parsedData.meta) {
        dataToRender = parsedData.meta;
      } else if (typeof parsedData === 'object') {
        // Convert object to array format
        dataToRender = Object.entries(parsedData).map(([key, value]) => ({ key, value }));
      } else {
        dataToRender = [parsedData];
      }

      // Ensure data is in the correct format
      if (!Array.isArray(dataToRender) || dataToRender.length === 0) {
        return <p className='text-gray-500'>No valid data to display. Please ensure data is an array.</p>;
      }

      return (
        <div className='w-full'>
          <TableData
            data={dataToRender}
            isFetching={false}
            isError={false}
            exapandable={true}
            pageSizeLimit={10}
            className='w-full'
          />
        </div>
      );
    } catch (error) {
      console.error('Error rendering TableData:', error);
      return (
        <div className='p-4 bg-red-50 border border-red-200 rounded'>
          <p className='text-red-800'>Error rendering TableData component</p>
          <p className='text-sm text-red-600 mt-1'>{String(error)}</p>
        </div>
      );
    }
  };

  const renderDynamicTable = () => {
    if (!parsedData || !Array.isArray(parsedData)) {
      return <p className='text-gray-500'>Please load array data for Dynamic Table</p>;
    }

    return (
      <div>
        <DynamicTable
          data={parsedData}
          showPagination={true}
          customSetValueFn={(rows: any[]) => {
            setSelectedRows(rows);
            toast.info(`Selected ${rows.length} rows`);
          }}
        />
        {selectedRows.length > 0 && (
          <div className='mt-4 p-3 bg-blue-50 border border-blue-200 rounded'>
            <p className='text-sm text-blue-800 font-medium'>Selected Rows:</p>
            <pre className='text-xs mt-2'>{JSON.stringify(selectedRows, null, 2)}</pre>
          </div>
        )}
      </div>
    );
  };

  const renderTextAreaDemo = () => {
    return (
      <div className='space-y-4'>
        <div>
          <h3 className='text-sm font-medium text-gray-700 mb-2'>Basic TextArea</h3>
          <TextArea
            rows={4}
            value={textAreaValue}
            onChange={(e) => setTextAreaValue(e.target.value)}
            placeholder='Type something here...'
          />
        </div>

        <div>
          <h3 className='text-sm font-medium text-gray-700 mb-2'>TextArea with Character Count</h3>
          <TextArea
            rows={4}
            maxLength={100}
            showCount
            value={textAreaValue}
            onChange={(e) => setTextAreaValue(e.target.value)}
            placeholder='Limited to 100 characters...'
          />
        </div>

        <div>
          <h3 className='text-sm font-medium text-gray-700 mb-2'>Auto-resize TextArea</h3>
          <TextArea
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={textAreaValue}
            onChange={(e) => setTextAreaValue(e.target.value)}
            placeholder='This will auto-resize as you type...'
          />
        </div>

        <div>
          <h3 className='text-sm font-medium text-gray-700 mb-2'>Disabled TextArea</h3>
          <TextArea
            rows={3}
            disabled
            value='This TextArea is disabled'
          />
        </div>

        {textAreaValue && (
          <div className='p-3 bg-gray-50 border border-gray-200 rounded'>
            <p className='text-sm text-gray-700 font-medium'>Current Value:</p>
            <p className='text-sm text-gray-600 mt-1 whitespace-pre-wrap'>{textAreaValue}</p>
          </div>
        )}
      </div>
    );
  }

  const renderCustomCharts = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load chart data first</p>;
    }

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Bar Chart</h3>
          <CustomBarChart
            data={parsedData}
            height={300}
            showGrid={true}
            showTooltip={true}
            showLegend={false}
          />
        </div>

        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Multi-Bar Chart</h3>
          <CustomBarChart
            data={parsedData}
            height={300}
            multiBar={true}
            showGrid={true}
            showTooltip={true}
            showLegend={true}
          />
        </div>

        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Area Chart</h3>
          <CustomAreaChart
            data={parsedData}
            height={300}
            showGrid={true}
            showTooltip={true}
            showLegend={false}
          />
        </div>

        {/* Code-Level Logic Explanation */}
        <Collapse
          items={[{
            key: 'code-logic',
            label: <div className='flex items-center gap-2'><span className='text-base'>💻</span><span className='font-semibold text-gray-800'>How It Works (Code Level)</span></div>,
            children: (
              <div className='space-y-4'>
                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>1. Data Processing & Scaling</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Extract all numeric keys from data
const yKeys = Object.keys(data[0]).filter(key =>
  typeof data[0][key] === 'number'
)

// Find maximum value for scaling
const maxValue = data.reduce((max, item) => {
  const itemMax = yKeys.reduce((keyMax, key) =>
    Math.max(keyMax, item[key] || 0), 0
  )
  return Math.max(max, itemMax)
}, 0) * 1.1  // Add 10% padding

// Calculate bar height based on value ratio
const getBarHeight = (value) => {
  const ratio = value / maxValue
  return chartHeight * ratio
}`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>2. Bar Positioning (Multi-Bar Mode)</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Calculate bar width and spacing
const barGroupWidth = chartWidth / data.length
const barCount = yKeys.length
const barWidth = Math.min(
  (barGroupWidth - GAP) / barCount,
  MAX_BAR_WIDTH
)

// Position bars side by side
const getBarX = (dataIndex, keyIndex) => {
  const groupX = margins.left + dataIndex * barGroupWidth
  return groupX + keyIndex * barWidth
}`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>3. Area Chart Fill</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Create path for area fill
const areaPath = data.map((item, i) => {
  const x = margins.left + i * barGroupWidth
  const y = getY(item[yKey])
  return \`\${i === 0 ? 'M' : 'L'} \${x} \${y}\`
}).join(' ')

// Close path to bottom for fill
const closedPath = \`\${areaPath} L \${endX} \${bottom} L \${startX} \${bottom} Z\`

// Render with gradient
<path d={closedPath} fill="url(#gradient)" />`}
                  </pre>
                </div>
              </div>
            )
          }]}
          className='border border-blue-300'
          defaultActiveKey={[]}
        />
      </div>
    );
  }

  const renderDualAxisChart = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load dual-axis chart data first</p>;
    }

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Dual-Axis Chart (Bar + Line Combo)</h3>
          <p className='text-sm text-gray-600 mb-4'>
            This chart automatically detects value disparity and displays high values as bars (left axis)
            and low values as lines (right axis). Great for comparing metrics with different scales.
          </p>
          <CustomBarChart
            data={parsedData}
            height={400}
            multiBar={true}
            enableDualAxis={true}
            dualAxisThreshold={10}
            showGrid={true}
            showTooltip={true}
            showLegend={true}
          />
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Features:</h4>
          <ul className='text-xs text-gray-600 space-y-1'>
            <li>• Automatically detects value disparity (default 10x difference)</li>
            <li>• High values displayed as bars on left Y-axis</li>
            <li>• Low values displayed as lines on right Y-axis</li>
            <li>• Perfect for metrics like Revenue ($$$) vs Orders (#) vs Conversion Rate (%)</li>
            <li>• Supports multiple series with automatic grouping</li>
            <li>• Interactive tooltips and legend</li>
          </ul>
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Sample Data Structure:</h4>
          <pre className='text-xs text-gray-600 overflow-x-auto'>
{`[
  {
    month: 'Jan',
    revenue: 125000,    // High value - displayed as bar
    orders: 450,        // Low value - displayed as line
    conversionRate: 3.2 // Low value - displayed as line
  },
  // ... more data points
]`}
          </pre>
        </div>

        {/* Code-Level Logic Explanation - Collapsible */}
        <Collapse
          items={[
            {
              key: 'code-logic',
              label: (
                <div className='flex items-center gap-2'>
                  <span className='text-base'>💻</span>
                  <span className='font-semibold text-gray-800'>How It Works (Code Level)</span>
                </div>
              ),
              children: (
                <div className='space-y-4'>
                  {/* Step 1: Detection Logic */}
                  <div className='bg-white rounded-lg p-4 border border-gray-200'>
                    <h5 className='text-sm font-bold text-gray-800 mb-2'>1. Automatic Detection Logic</h5>
                    <p className='text-xs text-gray-600 mb-2'>
                      The chart analyzes your data to determine if dual-axis mode should be enabled:
                    </p>
                    <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Calculate average for each series
const seriesStats = yKeys.map(key => {
  const values = data.map(item => item[key] || 0)
  const avg = sum / values.length
  return { key, avg, max }
})

// Example with your data:
// revenue:        avg = ~171,000
// orders:         avg = ~633
// conversionRate: avg = ~3.98

// Find max average
const maxAvg = Math.max(...seriesStats.map(s => s.avg))  // 171,000

// Check if any series is 10x smaller than max
const hasLargeDisparity = seriesStats.some(s =>
  s.avg > 0 && maxAvg / s.avg > dualAxisThreshold  // 10x
)

// Check: 171,000 / 633 = 270x ✓ (exceeds 10x)
// Check: 171,000 / 3.98 = 42,965x ✓ (exceeds 10x)`}
                    </pre>
                    <div className='mt-2 p-2 bg-blue-50 rounded text-[10px] text-blue-800'>
                      <strong>Key:</strong> If any series has an average that's 10x (or more) smaller than the max average, dual-axis activates.
                    </div>
                  </div>

                  {/* Step 2: Grouping */}
                  <div className='bg-white rounded-lg p-4 border border-gray-200'>
                    <h5 className='text-sm font-bold text-gray-800 mb-2'>2. Split Into High/Low Groups</h5>
                    <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Threshold = maxAvg / 10 = 171,000 / 10 = 17,100

const highValueKeys = seriesStats
  .filter(s => s.avg >= threshold)  // >= 17,100
  .map(s => s.key)
// Result: ['revenue']

const lowValueKeys = seriesStats
  .filter(s => s.avg < threshold)   // < 17,100
  .map(s => s.key)
// Result: ['orders', 'conversionRate']`}
                    </pre>
                  </div>

                  {/* Step 3: Y-Axis Scales */}
                  <div className='bg-white rounded-lg p-4 border border-gray-200'>
                    <h5 className='text-sm font-bold text-gray-800 mb-2'>3. Separate Y-Axis Scales</h5>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                      <div>
                        <p className='text-xs font-semibold text-gray-700 mb-1'>Left Axis (Bars):</p>
                        <pre className='text-[10px] bg-gray-50 p-2 rounded border border-gray-200'>
{`// maxHighValue = max(revenue) * 1.1
//               = 215,000 * 1.1
//               = 236,500

const getBarHeight = (value) => {
  const ratio = value / maxHighValue
  return chartHeight * ratio
}

// Example: revenue = 125,000
// ratio = 125,000 / 236,500 = 0.528
// height = 300px * 0.528 = 158px`}
                        </pre>
                      </div>
                      <div>
                        <p className='text-xs font-semibold text-gray-700 mb-1'>Right Axis (Lines):</p>
                        <pre className='text-[10px] bg-gray-50 p-2 rounded border border-gray-200'>
{`// maxLowValue = max(orders) * 1.1
//              = 820 * 1.1 = 902

const getLineY = (value) => {
  const ratio = value / maxLowValue
  return top + height * (1 - ratio)
}

// Example: orders = 450
// ratio = 450 / 902 = 0.499
// y = 20 + 300 * 0.501 = 170px`}
                        </pre>
                      </div>
                    </div>
                  </div>

                  {/* Step 4: Rendering */}
                  <div className='bg-white rounded-lg p-4 border border-gray-200'>
                    <h5 className='text-sm font-bold text-gray-800 mb-2'>4. Rendering Logic</h5>
                    <div className='space-y-2'>
                      <div>
                        <p className='text-xs font-semibold text-green-700 mb-1'>Bars (High Values):</p>
                        <pre className='text-[10px] bg-gray-50 p-2 rounded border border-gray-200'>
{`{highValueKeys.map((key) => {  // ['revenue']
  const value = item[key]
  const barY = getBarY(value)    // Use LEFT axis
  const barHeight = getBarHeight(value)

  return <rect y={barY} height={barHeight} fill={color} />
})}`}
                        </pre>
                      </div>
                      <div>
                        <p className='text-xs font-semibold text-orange-700 mb-1'>Lines (Low Values):</p>
                        <pre className='text-[10px] bg-gray-50 p-2 rounded border border-gray-200'>
{`{lowValueKeys.map((key) => {  // ['orders', 'conversionRate']
  const pathData = data.map((item) => {
    const value = item[key]
    const y = getLineY(value)  // Use RIGHT axis
    return { x, y, value }
  })

  // Create smooth curved path with Bézier curves
  return <path d={smoothPath} stroke={color} />
})}`}
                        </pre>
                      </div>
                    </div>
                  </div>

                  {/* Step 5: Visual Result */}
                  <div className='bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-gray-300'>
                    <h5 className='text-sm font-bold text-gray-800 mb-2'>5. Visual Result</h5>
                    <div className='space-y-2 text-xs text-gray-700'>
                      <div className='flex items-center gap-2'>
                        <div className='w-3 h-6 bg-green-600 rounded'></div>
                        <span><strong>Revenue (bars)</strong> → Left Y-axis: 0 - 236K</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <div className='w-8 h-1 bg-orange-500 rounded'></div>
                        <span><strong>Orders (line)</strong> → Right Y-axis: 0 - 902</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <div className='w-8 h-1 bg-yellow-500 rounded'></div>
                        <span><strong>ConversionRate (line)</strong> → Right Y-axis: 0 - 902</span>
                      </div>
                      <div className='mt-3 p-2 bg-white rounded text-[11px] text-gray-600 border border-gray-200'>
                        This allows you to compare metrics with <strong>vastly different scales</strong> on the same chart!
                      </div>
                    </div>
                  </div>

                  {/* Props Section */}
                  <div className='bg-white rounded-lg p-4 border border-gray-200'>
                    <h5 className='text-sm font-bold text-gray-800 mb-2'>6. Component Props</h5>
                    <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`<CustomBarChart
  data={parsedData}
  multiBar={true}              // Required: enable multi-series mode
  enableDualAxis={true}        // Enable dual-axis feature
  dualAxisThreshold={10}       // Trigger threshold (10x difference)
  showGrid={true}
  showTooltip={true}
  showLegend={true}
/>`}
                    </pre>
                    <div className='mt-2 text-[10px] text-gray-600'>
                      <p><strong>enableDualAxis:</strong> Enable/disable dual-axis mode (default: false)</p>
                      <p><strong>dualAxisThreshold:</strong> Ratio to trigger dual-axis (default: 10x). Set to <code className='bg-gray-200 px-1 rounded'>false</code> to disable.</p>
                    </div>
                  </div>
                </div>
              ),
            },
          ]}
          className='border border-blue-300'
          defaultActiveKey={[]}
        />
      </div>
    );
  }

  const renderMultiBarSecondaryYAxis = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load multi-bar-chart-secondary-y-axis data first</p>;
    }

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Multi-Bar Chart with Secondary Y-Axis (Line)</h3>
          <p className='text-sm text-gray-600 mb-4'>
            This chart displays all numeric keys except the <strong>LAST</strong> as bars on the primary y-axis,
            and the <strong>LAST key</strong> as a line on the secondary y-axis. The last key is always treated
            as the line, regardless of values. Perfect for showing multiple related metrics with a KPI trend line.
          </p>
          <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4'>
            <p className='text-xs text-yellow-900'>
              <strong>💡 Sample Data:</strong> In the data below, <code className='bg-yellow-100 px-1 rounded'>revenue</code>,
              <code className='bg-yellow-100 px-1 rounded'>expenses</code>, and <code className='bg-yellow-100 px-1 rounded'>profit</code>
              are shown as bars, while <code className='bg-green-100 px-1 rounded'>growthRate</code> (the LAST key)
              is shown as a line on the secondary y-axis.
            </p>
          </div>
          <CustomMultiBarChartWithLine
            data={parsedData}
            height={400}
            showGrid={true}
            showTooltip={true}
            showLegend={true}
          />
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Features:</h4>
          <ul className='text-xs text-gray-600 space-y-1'>
            <li>• Displays multiple metrics as bars (e.g., Revenue, Expenses, Profit)</li>
            <li>• <strong>LAST key in data object</strong> shown as a line on secondary y-axis (e.g., Growth Rate %)</li>
            <li>• Explicit key assignment - last key is ALWAYS the line, regardless of values</li>
            <li>• Perfect for comparing absolute values (dollars, units) with percentage/rate metrics</li>
            <li>• Interactive tooltips and legend with color coding</li>
            <li>• Responsive design with automatic dual-axis scaling</li>
          </ul>
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Sample Data Structure:</h4>
          <pre className='text-xs text-gray-600 overflow-x-auto'>
{`[
  {
    month: 'Jan',
    revenue: 125000,       // 📊 Bar chart (primary y-axis)
    expenses: 95000,       // 📊 Bar chart (primary y-axis)
    profit: 30000,         // 📊 Bar chart (primary y-axis)
    growthRate: 5.2        // 📈 LINE CHART (secondary y-axis) ← LAST KEY
  },
  {
    month: 'Feb',
    revenue: 148000,       // 📊 Bar
    expenses: 108000,      // 📊 Bar
    profit: 40000,         // 📊 Bar
    growthRate: 8.5        // 📈 LINE ← LAST KEY
  },
  // ... more data points
]

⚠️  IMPORTANT: The LAST numeric key in each object (growthRate) is
    ALWAYS rendered as a line on the secondary y-axis, regardless
    of its value. Key order matters!`}
          </pre>
        </div>

        <div className='bg-blue-50 rounded-lg p-4 border border-blue-200'>
          <h4 className='text-sm font-semibold text-blue-900 mb-2'>💡 Use Case Examples:</h4>
          <div className='text-xs text-blue-800 space-y-2'>
            <p>
              <strong>Financial Analysis:</strong> Show Revenue, Expenses, and Profit as bars, with Growth Rate %
              as a line to track performance trends.
            </p>
            <p>
              <strong>Sales Dashboard:</strong> Display sales by region (bars) with overall Conversion Rate % (line)
              to see which regions are most efficient.
            </p>
            <p>
              <strong>Marketing Metrics:</strong> Compare spending across channels (bars) with ROI % or
              Engagement Rate (line) to optimize budget allocation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderLineGraphSecondaryYAxis = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load line-graph-secondary-y-axis data first</p>;
    }

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Multi-Line Chart with Secondary Y-Axis</h3>
          <p className='text-sm text-gray-600 mb-4'>
            This chart displays all numeric keys as <strong>LINE CHARTS</strong>, with all keys except the <strong>LAST</strong>
            on the primary y-axis, and the <strong>LAST key</strong> on the secondary y-axis. Perfect for showing
            multiple related time-series metrics where one metric has a different scale.
          </p>
          <div className='bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4'>
            <p className='text-xs text-blue-900'>
              <strong>💡 Sample Data:</strong> In the data below, <code className='bg-blue-100 px-1 rounded'>temperature</code>,
              <code className='bg-blue-100 px-1 rounded'>humidity</code>, and <code className='bg-blue-100 px-1 rounded'>pressure</code>
              are shown as lines on the primary y-axis, while <code className='bg-green-100 px-1 rounded'>windSpeed</code> (the LAST key)
              is shown as a line on the secondary y-axis.
            </p>
          </div>
          <CustomMultiLineChartWithSecondaryAxis
            data={parsedData}
            height={400}
            showGrid={true}
            showTooltip={true}
            showLegend={true}
          />
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Features:</h4>
          <ul className='text-xs text-gray-600 space-y-1'>
            <li>• All metrics displayed as smooth line charts</li>
            <li>• <strong>All keys except LAST</strong> shown on primary y-axis</li>
            <li>• <strong>LAST key in data object</strong> shown on secondary y-axis</li>
            <li>• Ideal for multi-metric time-series data with different scales</li>
            <li>• Interactive tooltips showing all values at each point</li>
            <li>• Responsive design with automatic dual-axis scaling</li>
          </ul>
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Sample Data Structure:</h4>
          <pre className='text-xs text-gray-600 overflow-x-auto'>
{`[
  {
    month: 'Jan',
    temperature: 15,    // 📈 Line (primary y-axis)
    humidity: 65,       // 📈 Line (primary y-axis)
    pressure: 1013,     // 📈 Line (primary y-axis)
    windSpeed: 12       // 📈 LINE (secondary y-axis) ← LAST KEY
  },
  {
    month: 'Feb',
    temperature: 18,    // 📈 Line
    humidity: 62,       // 📈 Line
    pressure: 1015,     // 📈 Line
    windSpeed: 14       // 📈 LINE (secondary) ← LAST KEY
  },
  // ... more data points
]

⚠️  IMPORTANT: The LAST numeric key (windSpeed) is rendered on
    the secondary y-axis. All others on primary y-axis.`}
          </pre>
        </div>

        <div className='bg-purple-50 rounded-lg p-4 border border-purple-200'>
          <h4 className='text-sm font-semibold text-purple-900 mb-2'>💡 Use Case Examples:</h4>
          <div className='text-xs text-purple-800 space-y-2'>
            <p>
              <strong>Weather Data:</strong> Display temperature, humidity, and pressure on one axis, with wind speed
              on a secondary axis to see correlations between conditions.
            </p>
            <p>
              <strong>System Monitoring:</strong> Track CPU usage, memory, and disk I/O on primary axis, with network
              latency (ms) on secondary axis for comprehensive system health view.
            </p>
            <p>
              <strong>Website Analytics:</strong> Show page views, sessions, and bounce rate on one axis, with average
              session duration (seconds) on secondary axis for full engagement picture.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderBarGraphSecondaryYAxis = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load bar-graph-secondary-y-axis data first</p>;
    }

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Single Bar Chart with Secondary Y-Axis Line</h3>
          <p className='text-sm text-gray-600 mb-4'>
            This chart displays a <strong>single bar chart</strong> on the primary y-axis and a <strong>single line chart</strong>
            on the secondary y-axis. Perfect for showing one main metric with a related KPI or rate metric.
          </p>
          <div className='bg-green-50 border border-green-200 rounded-lg p-3 mb-4'>
            <p className='text-xs text-green-900'>
              <strong>💡 Sample Data:</strong> In the data below, <code className='bg-green-100 px-1 rounded'>sales</code> (first key)
              is shown as a bar chart on the primary y-axis, while <code className='bg-blue-100 px-1 rounded'>conversionRate</code> (second key)
              is shown as a line on the secondary y-axis.
            </p>
          </div>
          <CustomBarChartWithLine
            data={parsedData}
            height={400}
            showGrid={true}
            showTooltip={true}
            showLegend={true}
          />
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Features:</h4>
          <ul className='text-xs text-gray-600 space-y-1'>
            <li>• Single bar chart for the primary metric (first numeric key)</li>
            <li>• Single line chart for the secondary metric (second numeric key)</li>
            <li>• Automatic dual-axis scaling for metrics with different scales</li>
            <li>• Perfect for comparing absolute values with rates or percentages</li>
            <li>• Interactive tooltips for both bar and line</li>
            <li>• Clean, focused visualization with just two metrics</li>
          </ul>
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Sample Data Structure:</h4>
          <pre className='text-xs text-gray-600 overflow-x-auto'>
{`[
  {
    month: 'Jan',
    sales: 85000,          // 📊 Bar chart (primary y-axis) - FIRST KEY
    conversionRate: 3.2    // 📈 Line chart (secondary y-axis) - SECOND KEY
  },
  {
    month: 'Feb',
    sales: 92000,          // 📊 Bar
    conversionRate: 3.8    // 📈 Line
  },
  // ... more data points
]

⚠️  IMPORTANT: First numeric key = bar, Second numeric key = line`}
          </pre>
        </div>

        <div className='bg-teal-50 rounded-lg p-4 border border-teal-200'>
          <h4 className='text-sm font-semibold text-teal-900 mb-2'>💡 Use Case Examples:</h4>
          <div className='text-xs text-teal-800 space-y-2'>
            <p>
              <strong>Sales Dashboard:</strong> Display total sales (bar) with conversion rate % (line)
              to see both volume and efficiency trends.
            </p>
            <p>
              <strong>E-commerce:</strong> Show order volume (bar) with average order value (line)
              to understand both quantity and quality of sales.
            </p>
            <p>
              <strong>Marketing:</strong> Track impressions (bar) with click-through rate % (line)
              to balance reach with engagement effectiveness.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderPieChart = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load pie chart data first</p>;
    }

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Pie Chart</h3>
          <CustomPieChart
            data={parsedData}
            size={350}
            showTooltip={true}
            showLegend={true}
            showLabels={false}
          />
        </div>

        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Donut Chart</h3>
          <CustomPieChart
            data={parsedData}
            size={350}
            innerRadius={40}
            showTooltip={true}
            showLegend={true}
            showLabels={true}
          />
        </div>

        {/* Code-Level Logic Explanation */}
        <Collapse
          items={[{
            key: 'code-logic',
            label: <div className='flex items-center gap-2'><span className='text-base'>💻</span><span className='font-semibold text-gray-800'>How It Works (Code Level)</span></div>,
            children: (
              <div className='space-y-4'>
                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>1. Calculate Angles & Percentages</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Calculate total value
const total = data.reduce((sum, item) =>
  sum + (item.value || 0), 0
)

// Calculate angle for each slice
const dataWithAngles = data.map(item => {
  const percentage = (item.value / total) * 100
  const angle = (item.value / total) * 360
  return { ...item, percentage, angle }
})

// Example: Desktop = 45234 / 97498 = 46.4%
// Angle = 0.464 * 360 = 167°`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>2. Create SVG Arcs</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Track cumulative angle
let currentAngle = 0

data.map(item => {
  const startAngle = currentAngle
  const endAngle = currentAngle + item.angle

  // Convert to radians
  const start = (startAngle - 90) * (Math.PI / 180)
  const end = (endAngle - 90) * (Math.PI / 180)

  // Calculate arc coordinates
  const x1 = centerX + radius * Math.cos(start)
  const y1 = centerY + radius * Math.sin(start)
  const x2 = centerX + radius * Math.cos(end)
  const y2 = centerY + radius * Math.sin(end)

  // Large arc flag for angles > 180°
  const largeArc = item.angle > 180 ? 1 : 0

  currentAngle = endAngle
})`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>3. SVG Path Command</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Build path for pie slice
const pathData = [
  \`M \${centerX} \${centerY}\`,        // Move to center
  \`L \${x1} \${y1}\`,                    // Line to arc start
  \`A \${radius} \${radius} 0 \${largeArc} 1 \${x2} \${y2}\`,  // Arc
  'Z'                                 // Close path
].join(' ')

// For donut chart, add inner radius:
const donutPath = [
  \`M \${outerX1} \${outerY1}\`,
  \`A \${outerR} \${outerR} 0 \${largeArc} 1 \${outerX2} \${outerY2}\`,
  \`L \${innerX2} \${innerY2}\`,        // Connect to inner arc
  \`A \${innerR} \${innerR} 0 \${largeArc} 0 \${innerX1} \${innerY1}\`,
  'Z'
].join(' ')`}
                  </pre>
                </div>
              </div>
            )
          }]}
          className='border border-blue-300'
          defaultActiveKey={[]}
        />
      </div>
    );
  }

  const renderHorizontalBarChart = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load horizontal bar chart data first</p>;
    }

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Horizontal Bar Chart</h3>
          <CustomHorizontalBarChart
            data={parsedData}
            height={400}
            showGrid={true}
            showTooltip={true}
            showLegend={false}
          />
        </div>

        {parsedData.length > 0 && Object.keys(parsedData[0]).filter(key => typeof parsedData[0][key] === 'number').length > 1 && (
          <div>
            <h3 className='text-lg font-medium text-gray-700 mb-4'>Horizontal Multi-Bar Chart</h3>
            <CustomHorizontalBarChart
              data={parsedData}
              height={400}
              multiBar={true}
              showGrid={true}
              showTooltip={true}
              showLegend={true}
            />
          </div>
        )}

        {/* Code-Level Logic Explanation */}
        <Collapse
          items={[{
            key: 'code-logic',
            label: <div className='flex items-center gap-2'><span className='text-base'>💻</span><span className='font-semibold text-gray-800'>How It Works (Code Level)</span></div>,
            children: (
              <div className='space-y-4'>
                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>1. Horizontal Scale Calculation</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Find maximum value for X-axis scaling
const maxValue = data.reduce((max, item) => {
  const itemMax = Object.values(item)
    .filter(v => typeof v === 'number')
    .reduce((m, v) => Math.max(m, v), 0)
  return Math.max(max, itemMax)
}, 0) * 1.1  // Add 10% padding

// Calculate bar length based on value
const getBarWidth = (value) => {
  const ratio = value / maxValue
  return chartWidth * ratio
}

// Example: users = 68234, maxValue = 75000
// ratio = 68234 / 75000 = 0.91
// width = 600 * 0.91 = 546px`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>2. Vertical Bar Positioning</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Calculate vertical position for each bar
const barHeight = 20  // Fixed height per bar
const barGap = 10     // Gap between bars

const getBarY = (index) => {
  return margins.top + index * (barHeight + barGap)
}

// Total chart height needed
const chartHeight = data.length * (barHeight + barGap)

// Example: Bar 3 (index=2)
// y = 20 + 2 * (20 + 10) = 80px`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>3. Multi-Bar Horizontal Layout</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// For multi-bar mode, stack bars vertically per category
const yKeys = Object.keys(data[0])
  .filter(key => typeof data[0][key] === 'number')

const barGroupHeight = barHeight * yKeys.length

data.map((item, dataIndex) => {
  yKeys.map((key, keyIndex) => {
    const y = getBarY(dataIndex) + keyIndex * barHeight
    const width = getBarWidth(item[key])

    return (
      <rect
        x={margins.left}
        y={y}
        width={width}
        height={barHeight - 2}
        fill={colors[keyIndex]}
      />
    )
  })
})`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>4. Label Positioning</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Position labels on left side (category names)
const labelX = margins.left - 10  // 10px gap from bars
const labelY = getBarY(index) + barHeight / 2

<text
  x={labelX}
  y={labelY}
  textAnchor="end"        // Align text to right
  dominantBaseline="middle"
>
  {item.category}
</text>

// Position value labels inside bars (on right)
const valueX = margins.left + barWidth - 5
<text x={valueX} y={labelY} fill="white">
  {formatValue(item.value)}
</text>`}
                  </pre>
                </div>
              </div>
            )
          }]}
          className='border border-blue-300'
          defaultActiveKey={[]}
        />
      </div>
    );
  }

  const renderMultiHorizontalBarChart = () => {
    const multiBarData = parsedData || sampleData.multiBarData || [
      { country: 'India', activeUsers: 241835, newUsers: 228972, engagedSessions: 235229 },
      { country: 'United States', activeUsers: 120221, newUsers: 120221, engagedSessions: 235229 },
      { country: 'United Kingdom', activeUsers: 100221, newUsers: 95221, engagedSessions: 185229 },
      { country: 'Pakistan', activeUsers: 85221, newUsers: 80221, engagedSessions: 165229 },
      { country: 'Canada', activeUsers: 75221, newUsers: 70221, engagedSessions: 145229 },
      { country: 'Philippines', activeUsers: 65221, newUsers: 60221, engagedSessions: 125229 },
      { country: 'Nigeria', activeUsers: 55221, newUsers: 50221, engagedSessions: 105229 },
    ];

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Multi Horizontal Bar Chart (Custom SVG)</h3>
          <p className='text-sm text-gray-600 mb-4'>
            This chart uses custom SVG rendering with multiple data series displayed horizontally.
            Each country shows three metrics: Active Users, New Users, and Engaged Sessions.
          </p>
          <CustomMultiHorizontalBarChart
            data={multiBarData}
            height={500}
            barHeight={20}
            showGrid={true}
            showTooltip={true}
            showLegend={true}
            multiBar={true}
          />
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Features:</h4>
          <ul className='text-xs text-gray-600 space-y-1'>
            <li>• Automatic detection of numeric fields for multi-bar display</li>
            <li>• Hover tooltips showing exact values</li>
            <li>• Color-coded legend for each metric</li>
            <li>• Responsive sizing with ResizeObserver</li>
            <li>• Value formatting (K, M abbreviations)</li>
            <li>• Grid lines for better readability</li>
          </ul>
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Sample Data Structure:</h4>
          <pre className='text-xs text-gray-600 overflow-x-auto'>
{`[
  {
    country: 'India',
    activeUsers: 241835,
    newUsers: 228972,
    engagedSessions: 235229
  },
  {
    country: 'United States',
    activeUsers: 120221,
    newUsers: 120221,
    engagedSessions: 235229
  },
  // ... more countries
]`}
          </pre>
        </div>

        {/* Code-Level Logic Explanation */}
        <Collapse
          items={[{
            key: 'code-logic',
            label: <div className='flex items-center gap-2'><span className='text-base'>💻</span><span className='font-semibold text-gray-800'>How It Works (Code Level)</span></div>,
            children: (
              <div className='space-y-4'>
                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>1. Multi-Series Detection</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Detect all numeric fields (series)
const yKeys = Object.keys(data[0])
  .filter(key => typeof data[0][key] === 'number')

// Example result:
// yKeys = ['activeUsers', 'newUsers', 'engagedSessions']

// Find global max across all series
const maxValue = data.reduce((max, item) => {
  const itemMax = yKeys.reduce((m, key) =>
    Math.max(m, item[key] || 0), 0
  )
  return Math.max(max, itemMax)
}, 0) * 1.1

// Example: maxValue = 241835 * 1.1 = 266,019`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>2. Grouped Bar Layout</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Each country gets a vertical group of bars
const barHeight = 20
const barGap = 2
const groupGap = 15

const getGroupY = (dataIndex) => {
  const barsPerGroup = yKeys.length
  const groupHeight = barsPerGroup * (barHeight + barGap)
  return margins.top + dataIndex * (groupHeight + groupGap)
}

const getBarY = (dataIndex, keyIndex) => {
  return getGroupY(dataIndex) + keyIndex * (barHeight + barGap)
}

// Example: India (index=0), newUsers (keyIndex=1)
// groupY = 20 + 0 * (60 + 15) = 20
// barY = 20 + 1 * (20 + 2) = 42px`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>3. Rendering Each Series</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Render bars for each country and series
data.map((item, dataIndex) => {
  yKeys.map((key, keyIndex) => {
    const value = item[key]
    const barWidth = (value / maxValue) * chartWidth
    const y = getBarY(dataIndex, keyIndex)
    const color = colors[keyIndex % colors.length]

    return (
      <rect
        x={margins.left}
        y={y}
        width={barWidth}
        height={barHeight}
        fill={color}
      />
    )
  })
})

// Category labels (country names)
<text x={labelX} y={getGroupY(dataIndex) + groupHeight/2}>
  {item.country}
</text>`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>4. Legend & Color Assignment</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Color palette
const colors = [
  '#3b82f6',  // Blue for activeUsers
  '#10b981',  // Green for newUsers
  '#f59e0b'   // Orange for engagedSessions
]

// Legend items
yKeys.map((key, index) => {
  const color = colors[index % colors.length]
  const label = formatLabel(key)  // "Active Users"

  return (
    <div className='flex items-center gap-2'>
      <div style={{ background: color, width: 16, height: 16 }} />
      <span>{label}</span>
    </div>
  )
})`}
                  </pre>
                </div>
              </div>
            )
          }]}
          className='border border-blue-300'
          defaultActiveKey={[]}
        />
      </div>
    );
  }

  const renderMultiBarChart = () => {
    const multiBarData = parsedData || sampleData.multiBarData || [
      { month: 'Jan', sales: 241835, revenue: 228972, profit: 235229 },
      { month: 'Feb', sales: 120221, revenue: 120221, profit: 235229 },
      { month: 'Mar', sales: 100221, revenue: 95221, profit: 185229 },
      { month: 'Apr', sales: 85221, revenue: 80221, profit: 165229 },
      { month: 'May', sales: 75221, revenue: 70221, profit: 145229 },
      { month: 'Jun', sales: 65221, revenue: 60221, profit: 125229 },
      { month: 'Jul', sales: 55221, revenue: 50221, profit: 105229 },
    ];

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Multi Bar Chart (Vertical)</h3>
          <p className='text-sm text-gray-600 mb-4'>
            This chart uses custom SVG rendering with multiple data series displayed vertically.
            Each month shows three metrics: Sales, Revenue, and Profit.
          </p>
          <CustomMultiBarChart
            data={multiBarData}
            height={400}
            barWidth={32}
            showGrid={true}
            showTooltip={true}
            showLegend={true}
          />
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Features:</h4>
          <ul className='text-xs text-gray-600 space-y-1'>
            <li>• Automatic detection of numeric fields for multi-bar display</li>
            <li>• Grouped bars for easy comparison</li>
            <li>• Hover tooltips showing exact values</li>
            <li>• Color-coded legend for each metric</li>
            <li>• Responsive sizing with ResizeObserver</li>
            <li>• Value formatting (K, M abbreviations)</li>
          </ul>
        </div>

        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-semibold text-gray-700 mb-2'>Sample Data Structure:</h4>
          <pre className='text-xs text-gray-600 overflow-x-auto'>
{`[
  {
    month: 'Jan',
    sales: 241835,
    revenue: 228972,
    profit: 235229
  },
  {
    month: 'Feb',
    sales: 120221,
    revenue: 120221,
    profit: 235229
  },
  // ... more months
]`}
          </pre>
        </div>

        {/* Code-Level Logic Explanation */}
        <Collapse
          items={[{
            key: 'code-logic',
            label: <div className='flex items-center gap-2'><span className='text-base'>💻</span><span className='font-semibold text-gray-800'>How It Works (Code Level)</span></div>,
            children: (
              <div className='space-y-4'>
                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>1. Vertical Multi-Bar Grouping</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Detect all numeric series
const yKeys = Object.keys(data[0])
  .filter(key => typeof data[0][key] === 'number')

// Calculate bar positioning
const barWidth = 32  // Width of each bar
const barGap = 4     // Gap between bars in same group
const groupGap = 20  // Gap between different groups (months)

const groupWidth = yKeys.length * (barWidth + barGap) - barGap
const totalWidth = data.length * (groupWidth + groupGap)

// Calculate chart width
const chartWidth = Math.max(
  totalWidth,
  containerWidth - margins.left - margins.right
)`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>2. Bar Positioning Logic</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Position for each bar group (month)
const getGroupX = (dataIndex) => {
  return margins.left + dataIndex * (groupWidth + groupGap)
}

// Position for individual bar within group
const getBarX = (dataIndex, keyIndex) => {
  const groupX = getGroupX(dataIndex)
  return groupX + keyIndex * (barWidth + barGap)
}

// Example: Feb (index=1), revenue (keyIndex=1)
// groupX = 50 + 1 * (100 + 20) = 170
// barX = 170 + 1 * (32 + 4) = 206px`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>3. Y-Axis Scaling</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Find max value across all series
const maxValue = data.reduce((max, item) => {
  const itemMax = yKeys.reduce((m, key) =>
    Math.max(m, item[key] || 0), 0
  )
  return Math.max(max, itemMax)
}, 0) * 1.1  // Add 10% padding

// Calculate bar height
const getBarHeight = (value) => {
  const ratio = value / maxValue
  return chartHeight * ratio
}

const getBarY = (value) => {
  const height = getBarHeight(value)
  return chartTop + chartHeight - height
}

// Example: sales = 241835, maxValue = 266019
// ratio = 0.909, height = 363px, y = 37px`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>4. Rendering Grouped Bars</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Render all bars
data.map((item, dataIndex) => {
  yKeys.map((key, keyIndex) => {
    const value = item[key]
    const x = getBarX(dataIndex, keyIndex)
    const y = getBarY(value)
    const height = getBarHeight(value)
    const color = colors[keyIndex % colors.length]

    return (
      <rect
        x={x}
        y={y}
        width={barWidth}
        height={height}
        fill={color}
        rx={2}  // Rounded corners
      />
    )
  })
})

// X-axis labels (centered under each group)
<text x={getGroupX(index) + groupWidth/2} y={bottomY}>
  {item.month}
</text>`}
                  </pre>
                </div>
              </div>
            )
          }]}
          className='border border-blue-300'
          defaultActiveKey={[]}
        />
      </div>
    );
  }

  const renderDonutChart = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load donut chart data first</p>;
    }

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Donut Chart</h3>
          <CustomDonutChart
            data={parsedData}
            size={350}
            showTooltip={true}
            showLegend={true}
            showLabels={false}
            innerRadius={50}
          />
        </div>

        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Donut Chart with Labels</h3>
          <CustomDonutChart
            data={parsedData}
            size={350}
            showTooltip={true}
            showLegend={true}
            showLabels={true}
            innerRadius={60}
          />
        </div>

        {/* Code-Level Logic Explanation */}
        <Collapse
          items={[{
            key: 'code-logic',
            label: <div className='flex items-center gap-2'><span className='text-base'>💻</span><span className='font-semibold text-gray-800'>How It Works (Code Level)</span></div>,
            children: (
              <div className='space-y-4'>
                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>1. Donut vs Pie Chart</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Donut chart = Pie chart with inner radius
const outerRadius = size / 2 - padding
const innerRadius = 50  // Creates the "hole"

// For pie chart, innerRadius = 0
// For donut chart, innerRadius > 0

// Example: size = 350, padding = 20
// outerRadius = 350/2 - 20 = 155px
// innerRadius = 50px
// donut thickness = 155 - 50 = 105px`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>2. SVG Path for Donut Arc</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Calculate arc coordinates
const outerX1 = cx + outerRadius * Math.cos(startAngle)
const outerY1 = cy + outerRadius * Math.sin(startAngle)
const outerX2 = cx + outerRadius * Math.cos(endAngle)
const outerY2 = cy + outerRadius * Math.sin(endAngle)

const innerX1 = cx + innerRadius * Math.cos(startAngle)
const innerY1 = cy + innerRadius * Math.sin(startAngle)
const innerX2 = cx + innerRadius * Math.cos(endAngle)
const innerY2 = cy + innerRadius * Math.sin(endAngle)

// Large arc flag for angles > 180°
const largeArc = angle > 180 ? 1 : 0`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>3. Path Command Construction</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Build SVG path for donut slice
const pathData = [
  \`M \${outerX1} \${outerY1}\`,              // Move to outer arc start
  \`A \${outerRadius} \${outerRadius} 0 \${largeArc} 1 \${outerX2} \${outerY2}\`,  // Outer arc
  \`L \${innerX2} \${innerY2}\`,              // Line to inner arc
  \`A \${innerRadius} \${innerRadius} 0 \${largeArc} 0 \${innerX1} \${innerY1}\`,  // Inner arc (reverse)
  'Z'                                      // Close path
].join(' ')

// Note: Inner arc has sweep-flag=0 (counterclockwise)
// to go back to start point`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>4. Center Label Positioning</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Calculate label position (middle of donut ring)
const labelRadius = (outerRadius + innerRadius) / 2
const midAngle = (startAngle + endAngle) / 2

const labelX = cx + labelRadius * Math.cos(midAngle)
const labelY = cy + labelRadius * Math.sin(midAngle)

// Example: outerRadius=155, innerRadius=50
// labelRadius = (155 + 50) / 2 = 102.5px
// For 45° angle:
// labelX = 175 + 102.5 * cos(45°) = 247px
// labelY = 175 + 102.5 * sin(45°) = 247px

// Optional: Display total in center
<text x={cx} y={cy} textAnchor="middle">
  Total: {total}
</text>`}
                  </pre>
                </div>
              </div>
            )
          }]}
          className='border border-blue-300'
          defaultActiveKey={[]}
        />
      </div>
    );
  }

  const renderScatterPlot = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load scatter plot data first</p>;
    }

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Scatter Plot</h3>
          <CustomScatterPlot
            data={parsedData}
            xKey='price'
            yKey='sales'
            height={400}
            showGrid={true}
            showTooltip={true}
            showLegend={false}
            dotSize={5}
          />
        </div>

        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Grouped Scatter Plot</h3>
          <CustomScatterPlot
            data={parsedData}
            xKey='price'
            yKey='sales'
            groupKey='product'
            height={400}
            showGrid={true}
            showTooltip={true}
            showLegend={true}
            dotSize={6}
          />
        </div>

        {/* Code-Level Logic Explanation */}
        <Collapse
          items={[{
            key: 'code-logic',
            label: <div className='flex items-center gap-2'><span className='text-base'>💻</span><span className='font-semibold text-gray-800'>How It Works (Code Level)</span></div>,
            children: (
              <div className='space-y-4'>
                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>1. Calculate X & Y Scales</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Find min/max for X axis
const xValues = data.map(item => item[xKey])
const minX = Math.min(...xValues)
const maxX = Math.max(...xValues)
const xRange = maxX - minX

// Find min/max for Y axis
const yValues = data.map(item => item[yKey])
const minY = Math.min(...yValues)
const maxY = Math.max(...yValues)
const yRange = maxY - minY

// Add 10% padding
const xPadding = xRange * 0.1
const yPadding = yRange * 0.1`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>2. Map Data Points to Coordinates</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Scale X value to pixel position
const getX = (value) => {
  const ratio = (value - minX) / (maxX - minX + xPadding)
  return margins.left + ratio * chartWidth
}

// Scale Y value to pixel position (inverted)
const getY = (value) => {
  const ratio = (value - minY) / (maxY - minY + yPadding)
  return margins.top + chartHeight - (ratio * chartHeight)
}

// Example: price=15, sales=35
// x = 50 + (15-8)/(35-8) * 600 = 206px
// y = 20 + 400 - (35-15)/(40-15) * 400 = 100px`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>3. Group by Category (Optional)</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Group data points by groupKey if provided
const groups = groupKey ? data.reduce((acc, item) => {
  const group = item[groupKey]
  if (!acc[group]) acc[group] = []
  acc[group].push(item)
  return acc
}, {}) : { all: data }

// Assign different colors to each group
Object.keys(groups).map((groupName, index) => {
  const color = colors[index % colors.length]
  const points = groups[groupName]

  return points.map(point => (
    <circle
      cx={getX(point[xKey])}
      cy={getY(point[yKey])}
      r={dotSize}
      fill={color}
    />
  ))
})`}
                  </pre>
                </div>
              </div>
            )
          }]}
          className='border border-blue-300'
          defaultActiveKey={[]}
        />
      </div>
    );
  }

  const renderGeoMap = () => {
    if (!parsedData) {
      return <p className='text-gray-500'>Please load geographic data first</p>;
    }

    // Check if data is suitable for geo map (has country field)
    const isGeoData = Array.isArray(parsedData) &&
                      parsedData.length > 0 &&
                      parsedData.some(item => item.country || item.Country || item.COUNTRY);

    if (!isGeoData) {
      // Fallback to bar chart for non-geographic data
      return (
        <div className='space-y-4'>
          <div className='p-3 bg-yellow-50 border border-yellow-200 rounded'>
            <p className='text-sm text-yellow-800'>
              ⚠️ Data doesn't contain geographic information (no 'country' field found). Displaying as bar chart instead.
            </p>
          </div>

          <div>
            <h3 className='text-lg font-medium text-gray-700 mb-4'>Data Visualization (Bar Chart Fallback)</h3>
            <CustomBarChart
              data={parsedData}
              height={400}
              showGrid={true}
              showTooltip={true}
              showLegend={false}
            />
          </div>
        </div>
      );
    }

    return (
      <div className='space-y-8'>
        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Geographic Map</h3>
          <div style={{ position: 'relative' }}>
            <ErrorBoundary
              fallback={
                <div className='p-4 bg-red-50 border border-red-200 rounded'>
                  <p className='text-red-800'>Error rendering map. Trying alternative visualization...</p>
                  <div className='mt-4'>
                    <CustomBarChart
                      data={parsedData}
                      height={400}
                      showGrid={true}
                      showTooltip={true}
                      showLegend={false}
                    />
                  </div>
                </div>
              }
            >
              <CustomGeoMap
                data={parsedData}
                height={400}
                showTooltip={true}
              />
            </ErrorBoundary>
          </div>
        </div>

        <div>
          <h3 className='text-lg font-medium text-gray-700 mb-4'>Geographic Map (Custom Colors)</h3>
          <div style={{ position: 'relative' }}>
            <ErrorBoundary
              fallback={
                <div className='p-4 bg-red-50 border border-red-200 rounded'>
                  <p className='text-red-800'>Error rendering map. Showing data as horizontal bar chart...</p>
                  <div className='mt-4'>
                    <CustomHorizontalBarChart
                      data={parsedData}
                      height={400}
                      showGrid={true}
                      showTooltip={true}
                      showLegend={false}
                    />
                  </div>
                </div>
              }
            >
              <CustomGeoMap
                data={parsedData}
                height={400}
                showTooltip={true}
                colorScale={['hsl(200, 20%, 90%)', 'hsl(200, 70%, 40%)']}
              />
            </ErrorBoundary>
          </div>
        </div>

        {/* Code-Level Logic Explanation */}
        <Collapse
          items={[{
            key: 'code-logic',
            label: <div className='flex items-center gap-2'><span className='text-base'>💻</span><span className='font-semibold text-gray-800'>How It Works (Code Level)</span></div>,
            children: (
              <div className='space-y-4'>
                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>1. Country Name Matching</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Normalize country names for matching
const normalizeCountryName = (name) => {
  return name
    .toLowerCase()
    .replace(/[^a-z]/g, '')  // Remove special chars
}

// Match data to country geometries
const dataMap = data.reduce((acc, item) => {
  const normalized = normalizeCountryName(item.country)
  acc[normalized] = item.value
  return acc
}, {})

// Example:
// "United States" → "unitedstates"
// "United Kingdom" → "unitedkingdom"
// This allows flexible matching with GeoJSON data`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>2. Color Scale Calculation</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Find min and max values
const values = data.map(item => item.value)
const minValue = Math.min(...values)
const maxValue = Math.max(...values)

// Create color interpolation function
const getColor = (value) => {
  // Normalize value to 0-1 range
  const ratio = (value - minValue) / (maxValue - minValue)

  // Interpolate between color scale endpoints
  const startColor = parseColor(colorScale[0])  // Light
  const endColor = parseColor(colorScale[1])    // Dark

  return interpolateColor(startColor, endColor, ratio)
}

// Example: value = 75000, min = 10000, max = 125000
// ratio = (75000 - 10000) / (125000 - 10000) = 0.565
// Color: 56.5% between light and dark blue`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>3. SVG Path Rendering</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Load GeoJSON world map data
import worldMap from './world-map.geo.json'

// Project geographic coordinates to SVG coordinates
const projection = geoMercator()
  .scale(100)
  .translate([width / 2, height / 2])

// Create path generator
const pathGenerator = geoPath().projection(projection)

// Render each country
worldMap.features.map((feature) => {
  const countryName = normalizeCountryName(feature.properties.name)
  const value = dataMap[countryName]
  const color = value ? getColor(value) : '#e5e7eb'  // Gray for no data
  const pathData = pathGenerator(feature)

  return (
    <path
      d={pathData}
      fill={color}
      stroke="#fff"
      strokeWidth={0.5}
    />
  )
})`}
                  </pre>
                </div>

                <div className='bg-white rounded-lg p-4 border border-gray-200'>
                  <h5 className='text-sm font-bold text-gray-800 mb-2'>4. Interactive Tooltips</h5>
                  <pre className='text-[10px] bg-gray-50 p-3 rounded overflow-x-auto border border-gray-200'>
{`// Track hover state
const [hoveredCountry, setHoveredCountry] = useState(null)
const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })

// Mouse event handlers
<path
  d={pathData}
  fill={color}
  onMouseEnter={(e) => {
    setHoveredCountry({
      name: feature.properties.name,
      value: value
    })
    setTooltipPos({
      x: e.clientX,
      y: e.clientY
    })
  }}
  onMouseLeave={() => setHoveredCountry(null)}
/>

// Render tooltip
{hoveredCountry && (
  <div
    style={{
      position: 'fixed',
      left: tooltipPos.x + 10,
      top: tooltipPos.y + 10,
      background: 'rgba(0,0,0,0.8)',
      color: 'white',
      padding: '8px',
      borderRadius: '4px'
    }}
  >
    <strong>{hoveredCountry.name}</strong>
    <br />
    Value: {formatNumber(hoveredCountry.value)}
  </div>
)}`}
                  </pre>
                </div>
              </div>
            )
          }]}
          className='border border-blue-300'
          defaultActiveKey={[]}
        />
      </div>
    );
  }

  if (!userEmail || !userEmail.endsWith('@oogw.ai')) {
    return null;
  }

  return (
    <div className='min-h-screen bg-gray-50 p-6'>
      <div className='max-w-7xl mx-auto'>
        <div className='mb-6'>
          <h1 className='text-2xl font-bold text-gray-900'>Component Test Playground</h1>
          <p className='text-sm text-gray-600 mt-1'>Test various components with custom JSON data</p>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {/* JSON Input Section */}
          <div className='bg-white rounded-lg shadow p-6'>
            <h2 className='text-lg font-semibold mb-4'>JSON Data Input</h2>
            
            <div className='mb-4'>
              <label className='block text-lg font-semibold text-gray-800 mb-4'>
                Quick Load Sample Data
              </label>

              {/* Data Visualization Section */}
              <div className='mb-4'>

                {/* Single Axis Charts */}
                <div className='mb-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200'>
                  <p className='text-sm font-medium text-green-800 mb-3 flex items-center'>
                    <span className='mr-2 text-lg'>📊</span> Single Axis Charts
                  </p>
                  <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
                    {/* Bar Charts */}
                    <div className='space-y-2'>
                      <p className='text-xs font-medium text-gray-600 uppercase tracking-wide'>Bar Charts</p>
                      <Button
                        size='small'
                        className='!text-xs !px-3 !py-1 !h-7 w-full'
                        onClick={() => {
                          loadSampleData('verticalBarData');
                          setSelectedComponent('customCharts');
                        }}>
                        Vertical Bar
                      </Button>
                      <Button
                        size='small'
                        className='!text-xs !px-3 !py-1 !h-7 w-full'
                        onClick={() => {
                          loadSampleData('horizontalBarData');
                          setSelectedComponent('horizontalBar');
                        }}>
                        Horizontal Bar
                      </Button>
                      <Button
                        size='small'
                        className='!text-xs !px-3 !py-1 !h-7 w-full'
                        onClick={() => {
                          loadSampleData('multiBarData');
                          setSelectedComponent('multiBar');
                        }}>
                        Multi Vertical
                      </Button>
                      <Button
                        size='small'
                        className='!text-xs !px-3 !py-1 !h-7 w-full'
                        onClick={() => {
                          loadSampleData('multiBarData');
                          setSelectedComponent('multiHorizontalBar');
                        }}>
                        Multi Horizontal
                      </Button>
                    </div>

                    {/* Line & Area Charts */}
                    <div className='space-y-2'>
                      <p className='text-xs font-medium text-gray-600 uppercase tracking-wide'>Line & Area</p>
                      <Button
                        size='small'
                        className='!text-xs !px-3 !py-1 !h-7 w-full'
                        onClick={() => {
                          loadSampleData('areaChartData');
                          setSelectedComponent('customCharts');
                        }}>
                        Area Chart
                      </Button>
                    </div>

                    {/* Circular Charts */}
                    <div className='space-y-2'>
                      <p className='text-xs font-medium text-gray-600 uppercase tracking-wide'>Circular</p>
                      <Button
                        size='small'
                        className='!text-xs !px-3 !py-1 !h-7 w-full'
                        onClick={() => {
                          loadSampleData('pieChartData');
                          setSelectedComponent('pieChart');
                        }}>
                        Pie Chart
                      </Button>
                      <Button
                        size='small'
                        className='!text-xs !px-3 !py-1 !h-7 w-full'
                        onClick={() => {
                          loadSampleData('donutChartData');
                          setSelectedComponent('donutChart');
                        }}>
                        Donut Chart
                      </Button>
                    </div>

                    {/* Other Charts */}
                    <div className='space-y-2'>
                      <p className='text-xs font-medium text-gray-600 uppercase tracking-wide'>Other</p>
                      <Button
                        size='small'
                        className='!text-xs !px-3 !py-1 !h-7 w-full'
                        onClick={() => {
                          loadSampleData('scatterPlotData');
                          setSelectedComponent('scatterPlot');
                        }}>
                        Scatter Plot
                      </Button>
                      <Button
                        size='small'
                        className='!text-xs !px-3 !py-1 !h-7 w-full'
                        onClick={() => {
                          loadSampleData('geoMapData');
                          setSelectedComponent('geoMap');
                        }}>
                        Geo Map
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Dual Axis Charts - NEW SECTION */}
                <div className='mb-3 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200'>
                  <p className='text-sm font-medium text-purple-800 mb-3 flex items-center'>
                    <span className='mr-2 text-lg'>⚡</span> Dual Axis Charts
                  </p>
                  <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7 w-full bg-purple-100 hover:bg-purple-200'
                      onClick={() => {
                        loadSampleData('dualAxisData');
                        setSelectedComponent('dualAxis');
                      }}>
                      📊 Bar + Line
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7 w-full bg-purple-100 hover:bg-purple-200'
                      onClick={() => {
                        loadSampleData('multiBarSecondaryYAxis');
                        setSelectedComponent('multiBarSecondaryYAxis');
                      }}>
                      📊 Multi-Bar + Line
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7 w-full bg-purple-100 hover:bg-purple-200'
                      onClick={() => {
                        loadSampleData('barGraphSecondaryYAxis');
                        setSelectedComponent('barGraphSecondaryYAxis');
                      }}>
                      📊 Single Bar + Line
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7 w-full bg-purple-100 hover:bg-purple-200'
                      onClick={() => {
                        loadSampleData('lineGraphSecondaryYAxis');
                        setSelectedComponent('lineGraphSecondaryYAxis');
                      }}>
                      📈 Multi-Line Dual Axis
                    </Button>
                  </div>
                </div>

                {/* Visual Tags Subsection */}
                <div className='mb-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200'>
                  <p className='text-sm font-medium text-blue-800 mb-3 flex items-center'>
                    <span className='mr-2 text-lg'>🏷️</span> Visual Tags & Content Displays
                  </p>
                  <div className='grid grid-cols-2 md:grid-cols-3 gap-3'>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        loadSampleData('visualTag');
                        setSelectedComponent('visualTag');
                      }}>
                      Mixed Content
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        loadSampleData('visualTagText');
                        setSelectedComponent('visualTag');
                      }}>
                      Text Only
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        loadSampleData('visualTagTable');
                        setSelectedComponent('visualTag');
                      }}>
                      Table View
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        loadSampleData('visualTagLineGraph');
                        setSelectedComponent('visualTag');
                      }}>
                      Line Graph
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        loadSampleData('visualTagBarChart');
                        setSelectedComponent('visualTag');
                      }}>
                      Bar Chart
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        loadSampleData('visualTagMixed');
                        setSelectedComponent('visualTag');
                      }}>
                      Complex Mix
                    </Button>
                  </div>
                </div>
              </div>

              {/* Data Tables Section */}
              <div className='mb-4'>

                {/* Tables Subsection */}
                <div className='mb-3 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg border border-gray-200'>
                  <p className='text-sm font-medium text-gray-800 mb-3 flex items-center'>
                    <span className='mr-2 text-lg'>📊</span> Table Components
                  </p>
                  <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        loadSampleData('table');
                        setSelectedComponent('table');
                      }}>
                      Basic Table
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        loadSampleData('tableData');
                        setSelectedComponent('tableData');
                      }}>
                      TableData
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        loadSampleData('dynamicTable');
                        setSelectedComponent('dynamicTable');
                      }}>
                      Dynamic Table
                    </Button>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        loadSampleData('rawData');
                        setSelectedComponent('rawDataTable');
                      }}>
                      Raw Data
                    </Button>
                  </div>
                </div>
              </div>

              {/* Other Components Section */}
              <div className='mb-4'>
                <h3 className='text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider'>Other Components</h3>

                {/* Input Components Subsection */}
                <div className='mb-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200'>
                  <p className='text-sm font-medium text-purple-800 mb-3 flex items-center'>
                    <span className='mr-2 text-lg'>🔧</span> Input & Form Components
                  </p>
                  <div className='flex flex-wrap gap-2'>
                    <Button
                      size='small'
                      className='!text-xs !px-3 !py-1 !h-7'
                      onClick={() => {
                        setTextAreaValue(sampleData.textAreaSample);
                        setSelectedComponent('textArea');
                      }}>
                      TextArea
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                JSON Input (supports both JSON and Python dict format):
              </label>
              <TextArea
                rows={15}
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder={`Paste your JSON data here...

Examples:
1. Standard JSON: [{"category": "Sales", "value": 100}]
2. Python format: [{'category': 'Sales', 'value': 100}]
3. Campaign data: [{'category': 'Sale | WW | RT', 'value': 95}]`}
                className='font-mono text-xs'
              />
            </div>

            <div className='flex gap-2'>
              <Button type='primary' onClick={handleJsonParse}>
                Parse JSON
              </Button>
              <Button onClick={() => {
                setJsonInput('');
                setParsedData(null);
              }}>
                Clear
              </Button>
            </div>

            {parsedData && (
              <div className='mt-4 p-3 bg-green-50 border border-green-200 rounded'>
                <p className='text-sm text-green-800'>
                  ✓ JSON parsed successfully
                </p>
              </div>
            )}
          </div>

          {/* Component Preview Section */}
          <div className='bg-white rounded-lg shadow p-6'>
            <h2 className='text-lg font-semibold mb-4'>Component Preview</h2>

            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Select Component:
              </label>
              <Select
                className='w-full'
                value={selectedComponent}
                onChange={setSelectedComponent}
                options={[
                  { value: 'table', label: 'Basic Table' },
                  { value: 'tableData', label: 'TableData Component' },
                  { value: 'dynamicTable', label: 'Dynamic Table (NestedTable)' },
                  { value: 'visualTag', label: 'Visual Tag (Conversation)' },
                  { value: 'teamTable', label: 'Team Table' },
                  { value: 'rawDataTable', label: 'Raw Data Table' },
                  { value: 'textArea', label: 'TextArea Component' },
                  { value: 'customCharts', label: 'Custom Charts (Bar/Area)' },
                  { value: 'multiBarSecondaryYAxis', label: 'Multi-Bar + Secondary Y-Axis Line' },
                  { value: 'barGraphSecondaryYAxis', label: 'Single Bar + Secondary Y-Axis Line' },
                  { value: 'lineGraphSecondaryYAxis', label: 'Multi-Line + Secondary Y-Axis' },
                  { value: 'dualAxis', label: 'Dual-Axis Chart (Bar + Line)' },
                  { value: 'pieChart', label: 'Pie Chart' },
                  { value: 'donutChart', label: 'Donut Chart' },
                  { value: 'scatterPlot', label: 'Scatter Plot' },
                  { value: 'geoMap', label: 'Geographic Map' },
                  { value: 'horizontalBar', label: 'Horizontal Bar Chart' },
                  { value: 'multiHorizontalBar', label: 'Multi Horizontal Bar Chart' },
                  { value: 'multiBar', label: 'Multi Bar Chart (Vertical)' },
                ]}
              />
            </div>

            <div className='border rounded-lg p-4 min-h-[400px] overflow-auto'>
              {selectedComponent === 'table' && renderTableComponent()}
              {selectedComponent === 'tableData' && renderTableData()}
              {selectedComponent === 'dynamicTable' && renderDynamicTable()}
              {selectedComponent === 'visualTag' && renderVisualTag()}
              {selectedComponent === 'teamTable' && renderTeamTable()}
              {selectedComponent === 'rawDataTable' && renderRawDataTable()}
              {selectedComponent === 'textArea' && renderTextAreaDemo()}
              {selectedComponent === 'customCharts' && renderCustomCharts()}
              {selectedComponent === 'dualAxis' && renderDualAxisChart()}
              {selectedComponent === 'multiBarSecondaryYAxis' && renderMultiBarSecondaryYAxis()}
              {selectedComponent === 'barGraphSecondaryYAxis' && renderBarGraphSecondaryYAxis()}
              {selectedComponent === 'lineGraphSecondaryYAxis' && renderLineGraphSecondaryYAxis()}
              {selectedComponent === 'pieChart' && renderPieChart()}
              {selectedComponent === 'horizontalBar' && renderHorizontalBarChart()}
              {selectedComponent === 'multiHorizontalBar' && renderMultiHorizontalBarChart()}
              {selectedComponent === 'multiBar' && renderMultiBarChart()}
              {selectedComponent === 'donutChart' && renderDonutChart()}
              {selectedComponent === 'scatterPlot' && renderScatterPlot()}
              {selectedComponent === 'geoMap' && renderGeoMap()}
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className='mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4'>
          <h3 className='text-sm font-semibold text-blue-900 mb-2'>Available Components:</h3>
          <ul className='text-sm text-blue-800 space-y-1'>
            <li>• <strong>Basic Table:</strong> Generic table component with customizable rendering</li>
            <li>• <strong>TableData:</strong> Main table component used across the app with expand/collapse</li>
            <li>• <strong>Dynamic Table:</strong> Auto-generated table from NestedTable with row selection</li>
            <li>• <strong>Visual Tag:</strong> Conversation screen component for displaying formatted responses</li>
            <li>• <strong>Team Table:</strong> Specialized table for team member management</li>
            <li>• <strong>Raw Data Table:</strong> Integration-specific data display table</li>
            <li>• <strong>TextArea:</strong> Ant Design TextArea with various configurations</li>
            <li>• <strong>Custom Charts:</strong> Bar and Area charts with responsive design</li>
            <li>• <strong>Dual-Axis Chart:</strong> Combined bar + line chart for comparing metrics with different scales</li>
            <li>• <strong>Pie Chart:</strong> Circular chart with legend and tooltips</li>
            <li>• <strong>Donut Chart:</strong> Pie chart with hollow center</li>
            <li>• <strong>Scatter Plot:</strong> X-Y plot with grouping support</li>
            <li>• <strong>Geographic Map:</strong> Country-based data visualization</li>
            <li>• <strong>Horizontal Bar:</strong> Horizontal bar charts with multi-series support</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ComponentTestPlayground;